import React, { useState } from 'react';
import { RefreshCw, Trash2, CheckCircle } from 'lucide-react';
import { logger } from '../utils/logger';

/**
 * CacheManager Component
 *
 * Provides manual cache clearing and service worker management for testing.
 * This helps developers test PWA updates without waiting for automatic updates.
 *
 * Usage: Add to App.js or Settings for testing, remove for production.
 */
const CacheManager = () => {
  const [clearing, setClearing] = useState(false);
  const [message, setMessage] = useState('');

  const clearAllCaches = async () => {
    setClearing(true);
    setMessage('');

    try {
      // Clear all caches
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map(cacheName => {
          logger.debug('Deleting cache:', cacheName);
          return caches.delete(cacheName);
        })
      );

      // Unregister service worker
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(
          registrations.map(registration => {
            logger.debug('Unregistering service worker');
            return registration.unregister();
          })
        );
      }

      setMessage('✅ All caches cleared! Reload the page to get the latest version.');

      // Auto-reload after 2 seconds
      setTimeout(() => {
        window.location.reload(true);
      }, 2000);

    } catch (error) {
      logger.error('Error clearing caches:', error);
      setMessage('❌ Error clearing caches. Check console for details.');
    } finally {
      setClearing(false);
    }
  };

  const forceUpdate = async () => {
    setClearing(true);
    setMessage('');

    try {
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration) {
          await registration.update();
          setMessage('✅ Update check complete! If new version available, it will install.');
        } else {
          setMessage('ℹ️ No service worker registered.');
        }
      } else {
        setMessage('ℹ️ Service workers not supported.');
      }
    } catch (error) {
      logger.error('Error updating service worker:', error);
      setMessage('❌ Error checking for updates.');
    } finally {
      setClearing(false);
    }
  };

  return (
    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
      <div className="flex items-center gap-2 mb-3">
        <RefreshCw className="w-5 h-5 text-yellow-600" />
        <h3 className="font-semibold text-yellow-900">Cache Manager (Testing Only)</h3>
      </div>

      <p className="text-sm text-yellow-800 mb-3">
        Use these tools to test PWA updates. Remove this component before production deployment.
      </p>

      <div className="flex flex-wrap gap-2">
        <button
          onClick={clearAllCaches}
          disabled={clearing}
          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
        >
          <Trash2 className="w-4 h-4" />
          Clear All Caches
        </button>

        <button
          onClick={forceUpdate}
          disabled={clearing}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          <CheckCircle className="w-4 h-4" />
          Check for Updates
        </button>
      </div>

      {message && (
        <div className="mt-3 p-3 bg-white border border-yellow-300 rounded text-sm text-gray-800">
          {message}
        </div>
      )}

      <details className="mt-3">
        <summary className="text-xs text-yellow-700 cursor-pointer hover:underline">
          View Cache Status
        </summary>
        <div className="mt-2 p-2 bg-white rounded text-xs font-mono">
          <CacheStatus />
        </div>
      </details>
    </div>
  );
};

const CacheStatus = () => {
  const [status, setStatus] = React.useState('Loading...');

  React.useEffect(() => {
    const checkStatus = async () => {
      try {
        const cacheNames = await caches.keys();
        let registration = null;

        if ('serviceWorker' in navigator) {
          registration = await navigator.serviceWorker.getRegistration();
        }

        const info = {
          caches: cacheNames,
          serviceWorker: registration ? 'Registered' : 'Not registered',
          controller: navigator.serviceWorker?.controller ? 'Active' : 'No controller'
        };

        setStatus(JSON.stringify(info, null, 2));
      } catch (error) {
        setStatus(`Error: ${error.message}`);
      }
    };

    checkStatus();
  }, []);

  return <pre className="text-gray-700">{status}</pre>;
};

export default CacheManager;
